import { Box, Button, TextField, Typography, WithStyles } from '@material-ui/core';
import { createStyles, withStyles } from '@material-ui/core/styles';
import { CancelOutlined } from '@material-ui/icons';
import DomainIcon from '@material-ui/icons/Domain';
import FileIcon from '@material-ui/icons/FileCopyOutlined';
import { Autocomplete } from '@mui/material';
import _ from 'lodash';
import moment from 'moment';
import React from 'react';
import { IDomainOptionDTO } from '../../repository/jago/model/input/IDomainOptionDTO';
import { IMksImportInDTO } from '../../repository/jago/model/input/IMksImportInDTO';
import { ICylindersRequestParamsDTO } from '../../repository/jago/model/output/RequestParamsDTOs';
import { getDomainNameFromHostname } from '../../repository/utils';
import ReduxLanguage from '../ReduxLanguage';

const styles = createStyles({
  container: {
    display: 'flex',
    flexDirection: 'row',
    padding: 10,
    alignItems: 'center',
  },
  filterItem: {
    marginRight: 10,
    minWidth: 180,
  },
});

export interface ICylindersFilterBarProps extends WithStyles<typeof styles> {
  onSearch: (params: ICylindersRequestParamsDTO) => void;
  onResetFilters: () => void;
  onFilterDomains: (value: string) => void;
  onDomainSelect: (domain: IDomainOptionDTO | null) => void;
  domainsOptions: IDomainOptionDTO[];
  selectedHostname: string;
  selectedDomain: IDomainOptionDTO | null;
  isVisible?: boolean;
  lastImport?: IMksImportInDTO | {};
}

export interface ICylindersFilterBarState {
  domainSearchText: string;
  typeFilter: string;
  doorNameFilter: string;
}


class CylindersFilterBar extends React.Component<ICylindersFilterBarProps, ICylindersFilterBarState> {
  
  constructor(props: ICylindersFilterBarProps) {
    super(props);
    this.state = {
      domainSearchText: this.props.selectedDomain ? this.props.selectedDomain.label : '',
      typeFilter: '',
      doorNameFilter: '',
    }
  }

  componentDidUpdate(prevProps: ICylindersFilterBarProps) {
    // Aggiorna il domainSearchText se cambia il selectedDomain dal Redux
    if (prevProps.selectedDomain !== this.props.selectedDomain) {
      const { selectedDomain } = this.props;
      if (selectedDomain) {
        this.setState({ domainSearchText: selectedDomain.label });
      } else {
        this.setState({ domainSearchText: '' });
      }
    }
  }

  public onSearch() {
    const { onSearch, selectedDomain } = this.props;
    const { typeFilter, doorNameFilter } = this.state;
    
    if (!selectedDomain) return;

    const params: ICylindersRequestParamsDTO = {
      hostname: selectedDomain.value.hostname,
      page: 0,
      pageSize: 100,
      type: typeFilter || undefined,
      doorName: doorNameFilter || undefined,
    };
    
    onSearch(params);
  }

  public onResetFilters() {
    const { onResetFilters } = this.props;
    this.setState({ 
      domainSearchText: this.props.selectedDomain ? this.props.selectedDomain.label : '',
      typeFilter: '',
      doorNameFilter: '',
    });
    
    onResetFilters();
  }

  public onSelectDomain(domain: IDomainOptionDTO | null) {
    const { onDomainSelect, onResetFilters } = this.props;
    if (domain) {
      // Chiama la funzione passata dalla pagina per aggiornare il Redux
      onDomainSelect(domain);
      this.setState({ domainSearchText: domain.label }, () => {
        this.onSearch();
      });
    } else {
      // Quando si cancella la selezione, resettiamo
      this.setState({ domainSearchText: '' });
      onResetFilters();
    }
  }


  public onTypeFilterChange(text: string) {
    this.setState({ typeFilter: text }, () => {
      if (this.props.selectedDomain) {
        this.debouncedSearch();
      }
    });
  }

  public onDoorNameFilterChange(text: string) {
    this.setState({ doorNameFilter: text }, () => {
      if (this.props.selectedDomain) {
        this.debouncedSearch();
      }
    });
  }

  private debouncedSearch = _.debounce(() => {
    this.onSearch();
  }, 500);

  onFetchDomains(value: string) {
    const { onFilterDomains } = this.props;
    onFilterDomains(value);
  }

  public render() {
    const { classes, domainsOptions, selectedDomain, isVisible = true, lastImport } = this.props;
    const { domainSearchText, typeFilter, doorNameFilter } = this.state;
    const debouncedDomainSearch = _.debounce((text) => this.onFetchDomains(text), 500);
    return (
      <div className={classes.container} style={{ position: 'relative' }}>
        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 10, width: '100%' }}>
          <div className={classes.filterItem}>
            <Autocomplete
              options={domainsOptions}
              inputValue={domainSearchText}
              getOptionLabel={(option) => {
                if (!option) return '';
                if (option.label) return option.label;
                if (option.value?.name) return option.value.name;
                if (option.value?.hostname) return getDomainNameFromHostname(option.value.hostname);
                return '';
              }}
              isOptionEqualToValue={(option, value) => option?.value?.hostname === value?.value?.hostname}
              renderInput={(params) => (
                <TextField 
                  {...params} 
                  variant="outlined" 
                  label={<ReduxLanguage languageKey="mks.hostnameRequired" />}
                  required
                  error={!selectedDomain}
                  helperText={''}
                  style={{ minWidth: 250 }}
                />
              )}
              onInputChange={(event, newInputValue) => {
                this.setState({ domainSearchText: newInputValue }); 
                debouncedDomainSearch(newInputValue); 
              }}
              onChange={(event, newValue) => this.onSelectDomain(newValue)}
              noOptionsText="No domains found"
              loadingText="Loading domains..."
              clearOnBlur={false}
              clearOnEscape={true}
            />
          </div>
           {isVisible ? (
            <>
           <div className={classes.filterItem}>
             <TextField
               label={<ReduxLanguage languageKey="mks.filterByType" />}
               variant="outlined"
               value={typeFilter}
               onChange={(e) => this.onTypeFilterChange(e.target.value)}
               disabled={!selectedDomain}
               placeholder=""
             />
           </div>

           <div className={classes.filterItem}>
             <TextField
               label={<ReduxLanguage languageKey="mks.filterByDoorName" />}
               variant="outlined"
               value={doorNameFilter}
               onChange={(e) => this.onDoorNameFilterChange(e.target.value)}
               disabled={!selectedDomain}
               placeholder=""
             />
           </div>
           </>
           ) : (
            null
           )}
          <Button
            variant="contained"
            style={{ 
              backgroundColor: 'red', 
              height: 40, 
              color: 'white', 
              fontWeight: 'bold',
              minWidth: 'auto',
              padding: '8px 16px'
            }}
            onClick={() => this.onResetFilters()}
          >
            <CancelOutlined style={{ color: 'white' }} />
          </Button>
          {lastImport && Object.keys(lastImport).length > 0 && (
            <Box style={{ marginLeft: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', paddingRight: '10px' }}>
              <Typography variant="body2" style={{ fontWeight: 'bold', color: '#666', fontSize: 17 }}>
                <ReduxLanguage languageKey="mks.currentConfig" />
              </Typography>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <DomainIcon style={{ marginRight: 5, color: '#333', fontSize: 16 }} />
                <Typography variant="body2" style={{ color: '#333' }}>
                  {(getDomainNameFromHostname(selectedDomain?.value?.hostname))}
                </Typography>
              </div>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <FileIcon style={{ marginRight: 5, color: '#333', fontSize: 16 }} />
              <Typography variant="body2" style={{ color: '#333' }}>
                {(lastImport as IMksImportInDTO).filename}
              </Typography>
              </div>
              <Typography variant="caption" style={{ color: '#999' }}>
                {moment((lastImport as IMksImportInDTO).importTimestamp).format('DD/MM/YYYY HH:mm')}
              </Typography>
            </Box>
          )}
        </div>
      </div>
    );
  }
}

export default withStyles(styles)(CylindersFilterBar);
